/**
 * Endpoints de las últimas pantallas de **Gestión**: Créditos
 * (`/api/gestion/creditos*`), Clientes APP (`/api/gestion/clientes-app`) y
 * Administración (`/api/gestion/administracion/mapa-calor`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  BalanceCreditoRespuesta,
  CatalogosCreditoRespuesta,
  CatalogosClientesAppRespuesta,
  ClienteAppGeneral,
  ClientesAppGeneralesRespuesta,
  ClientesAppRespuesta,
  DatosClienteApp,
  DatosDescuentoApp,
  DatosVehiculoApp,
  CreditoDetalleRespuesta,
  CreditosCatalogosRespuesta,
  CreditosRespuesta,
  EdicionCredito,
  FiltrosBalance,
  FiltrosClientesApp,
  VehiculosClienteRespuesta,
  FiltrosCreditos,
  FiltrosPagos,
  FiltrosReportePagos,
  FlexibilidadRespuesta,
  FiltrosValesCanje,
  MapaCalorRespuesta,
  MetricaCalor,
  PagosCreditoRespuesta,
  LiquidacionRespuesta,
  NuevaLineaCredito,
  ParametrosCanjeRespuesta,
  PeticionLiquidarCanje,
  PeticionPagoCredito,
  PeticionPrecalculoCanje,
  PagoRegistradoRespuesta,
  PrecalculoCanjeRespuesta,
  ReportePagosRespuesta,
  SeriesCanjeRespuesta,
  ValesCanjeRespuesta,
} from './cierre-gestion.types';

const CLAVE = 'gestion';

export const cierreKeys = {
  creditos: (filtros: FiltrosCreditos) => [CLAVE, 'creditos', 'listado', filtros] as const,
  credito: (id: number | null, filtros: FiltrosCreditos) =>
    [CLAVE, 'creditos', 'detalle', id, filtros] as const,
  clientesApp: (filtros: FiltrosClientesApp) => [CLAVE, 'clientes-app', 'listado', filtros] as const,
  clientesAppGenerales: () => [CLAVE, 'clientes-app', 'clientes'] as const,
  vehiculosCliente: (clienteId: number | null) =>
    [CLAVE, 'clientes-app', 'vehiculos', clienteId] as const,
  catalogosClientesApp: () => [CLAVE, 'clientes-app', 'catalogos'] as const,
  calor: (year: number, month: number, metric: MetricaCalor) =>
    [CLAVE, 'administracion', 'calor', year, month, metric] as const,
  catalogos: () => [CLAVE, 'creditos', 'catalogos'] as const,
  balance: (id: number | null, filtros: FiltrosBalance) =>
    [CLAVE, 'creditos', 'balance', id, filtros] as const,
  vales: (id: number | null, filtros: FiltrosValesCanje) =>
    [CLAVE, 'creditos', 'vales', id, filtros] as const,
  seriesCanje: (tipo: string) => [CLAVE, 'creditos', 'series', tipo] as const,
  parametrosCanje: () => [CLAVE, 'creditos', 'parametros-canje'] as const,
  pagos: (id: number | null, filtros: FiltrosPagos) =>
    [CLAVE, 'creditos', 'pagos', id, filtros] as const,
  creditosCatalogos: () => [CLAVE, 'creditos', 'alta', 'catalogos'] as const,
  flexibilidad: (id: number | null) => [CLAVE, 'creditos', 'flexibilidad', id] as const,
  reportePagos: (filtros: FiltrosReportePagos) =>
    [CLAVE, 'creditos', 'reporte-pagos', filtros] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const cierreApi = {
  creditos: (filtros: FiltrosCreditos, signal?: AbortSignal): Promise<CreditosRespuesta> =>
    apiClient.get<CreditosRespuesta>(`/api/gestion/creditos${cadenaDe(filtros)}`, { signal }),

  /**
   * Estado de créditos en PDF (`Credito::status_pdf` del panel anterior).
   *
   * No sirve un `<a href>` directo: la API exige el token de la sesión y los
   * enlaces del navegador no lo envían, así que se pide con `fetch` y se guarda
   * el resultado como archivo.
   */
  pdfCreditos: async (filtros: FiltrosCreditos): Promise<void> => {
    const token = getSessionProvider()?.getAccessToken();
    const respuesta = await fetch(`${env.apiUrl}/api/gestion/creditos/pdf${cadenaDe(filtros)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (!respuesta.ok) {
      throw new Error(`No se pudo generar el PDF de créditos (HTTP ${respuesta.status}).`);
    }

    const blob = await respuesta.blob();
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');

    enlace.href = url;
    enlace.download = `estado-creditos-${filtros.desde ?? ''}_${filtros.hasta ?? ''}.pdf`;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(url);
  },

  /** Consulta el reporte general de pagos de todos los créditos del rango. */
  reportePagos: (
    filtros: FiltrosReportePagos,
    signal?: AbortSignal,
  ): Promise<ReportePagosRespuesta> =>
    apiClient.get<ReportePagosRespuesta>(
      `/api/gestion/creditos/reporte-pagos${cadenaDe(filtros)}`,
      { signal },
    ),

  /**
   * Descarga el reporte general de pagos en PDF (`Credito::report_pagos_pdf`,
   * A4 horizontal como el original).
   */
  pdfReportePagos: async (filtros: FiltrosReportePagos): Promise<void> => {
    const token = getSessionProvider()?.getAccessToken();
    const respuesta = await fetch(
      `${env.apiUrl}/api/gestion/creditos/reporte-pagos/pdf${cadenaDe(filtros)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
    );

    if (!respuesta.ok) {
      throw new Error(`No se pudo generar el reporte de pagos (HTTP ${respuesta.status}).`);
    }

    const blob = await respuesta.blob();
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');

    enlace.href = url;
    enlace.download = `reporte-pagos-${filtros.desde ?? ''}_${filtros.hasta ?? ''}.pdf`;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(url);
  },

  credito: (
    id: number,
    filtros: FiltrosCreditos,
    signal?: AbortSignal,
  ): Promise<CreditoDetalleRespuesta> =>
    apiClient.get<CreditoDetalleRespuesta>(
      `/api/gestion/creditos/${id}${cadenaDe(filtros)}`,
      { signal },
    ),

  clientesApp: (
    filtros: FiltrosClientesApp,
    signal?: AbortSignal,
  ): Promise<ClientesAppRespuesta> =>
    apiClient.get<ClientesAppRespuesta>(`/api/gestion/clientes-app${cadenaDe(filtros)}`, { signal }),

  clientesAppGenerales: (signal?: AbortSignal): Promise<ClientesAppGeneralesRespuesta> =>
    apiClient.get<ClientesAppGeneralesRespuesta>('/api/gestion/clientes-app/clientes', {
      signal,
    }),

  crearClienteApp: (
    datos: DatosClienteApp,
  ): Promise<{ cliente: ClienteAppGeneral; mensaje: string }> =>
    apiClient.post<{ cliente: ClienteAppGeneral; mensaje: string }>(
      '/api/gestion/clientes-app/clientes',
      { body: datos },
    ),
  editarClienteApp: (
    id: number,
    datos: DatosClienteApp,
  ): Promise<{ cliente: ClienteAppGeneral; mensaje: string }> =>
    apiClient.put<{ cliente: ClienteAppGeneral; mensaje: string }>(
      `/api/gestion/clientes-app/clientes/${id}`,
      { body: datos },
    ),

  catalogosClientesApp: (signal?: AbortSignal): Promise<CatalogosClientesAppRespuesta> =>
    apiClient.get<CatalogosClientesAppRespuesta>('/api/gestion/clientes-app/descuentos/catalogos', {
      signal,
    }),

  vehiculosDeCliente: (
    clienteId: number,
    signal?: AbortSignal,
  ): Promise<VehiculosClienteRespuesta> =>
    apiClient.get<VehiculosClienteRespuesta>(`/api/gestion/clientes-app/${clienteId}/vehiculos`, {
      signal,
    }),

  crearVehiculoApp: (clienteId: number, datos: DatosVehiculoApp): Promise<{ id: number }> =>
    apiClient.post<{ id: number }>(`/api/gestion/clientes-app/${clienteId}/vehiculos`, {
      body: datos,
    }),

  eliminarVehiculoApp: (
    vehiculoId: number,
  ): Promise<{ id: number; descuentos_desactivados: number; mensaje: string }> =>
    apiClient.delete<{ id: number; descuentos_desactivados: number; mensaje: string }>(
      `/api/gestion/clientes-app/vehiculos/${vehiculoId}`,
    ),

  crearDescuentoApp: (datos: DatosDescuentoApp): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/clientes-app/descuentos', {
      body: datos,
    }),

  actualizarDescuentoApp: (
    id: number,
    datos: DatosDescuentoApp,
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.put<{ id: number; mensaje: string }>(`/api/gestion/clientes-app/descuentos/${id}`, {
      body: datos,
    }),

  eliminarDescuentoApp: (id: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.delete<{ id: number; mensaje: string }>(
      `/api/gestion/clientes-app/descuentos/${id}`,
    ),

  eliminarDescuentosMasivo: (
    ids: number[],
  ): Promise<{ solicitados: number; desactivados: number; mensaje: string }> =>
    apiClient.post<{ solicitados: number; desactivados: number; mensaje: string }>(
      '/api/gestion/clientes-app/descuentos/masivo-eliminar',
      { body: { discount_ids: ids } },
    ),

  catalogos: (signal?: AbortSignal): Promise<CreditosCatalogosRespuesta> =>
    apiClient.get<CreditosCatalogosRespuesta>('/api/gestion/creditos/catalogos', { signal }),

  actualizarCredito: (
    id: number,
    datos: EdicionCredito,
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/creditos/${id}`, { body: datos }),

  balance: (
    id: number,
    filtros: FiltrosBalance,
    signal?: AbortSignal,
  ): Promise<BalanceCreditoRespuesta> =>
    apiClient.get<BalanceCreditoRespuesta>(
      `/api/gestion/creditos/${id}/balance${cadenaDe(filtros)}`,
      { signal },
    ),

  valesCanje: (
    id: number,
    filtros: FiltrosValesCanje,
    signal?: AbortSignal,
  ): Promise<ValesCanjeRespuesta> =>
    apiClient.get<ValesCanjeRespuesta>(
      `/api/gestion/creditos/${id}/vales${cadenaDe(filtros)}`,
      { signal },
    ),

  precalculoCanje: (
    id: number,
    datos: PeticionPrecalculoCanje,
  ): Promise<PrecalculoCanjeRespuesta> =>
    apiClient.post<PrecalculoCanjeRespuesta>(
      `/api/gestion/creditos/${id}/vales/precalculo`,
      { body: datos },
    ),

  seriesCanje: (tipo: string, signal?: AbortSignal): Promise<SeriesCanjeRespuesta> =>
    apiClient.get<SeriesCanjeRespuesta>(
      `/api/gestion/creditos/canje/series${cadenaDe({ tipo })}`,
      { signal },
    ),

  parametrosCanje: (signal?: AbortSignal): Promise<ParametrosCanjeRespuesta> =>
    apiClient.get<ParametrosCanjeRespuesta>('/api/gestion/creditos/canje/parametros', { signal }),

  crearCredito: (datos: NuevaLineaCredito): Promise<{ credito_id: number; mensaje: string }> =>
    apiClient.post<{ credito_id: number; mensaje: string }>('/api/gestion/creditos', { body: datos }),

  eliminarCredito: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/creditos/${id}`),

  catalogosCredito: (signal?: AbortSignal): Promise<CatalogosCreditoRespuesta> =>
    apiClient.get<CatalogosCreditoRespuesta>('/api/gestion/creditos/alta/catalogos', { signal }),

  flexibilidad: (id: number, signal?: AbortSignal): Promise<FlexibilidadRespuesta> =>
    apiClient.get<FlexibilidadRespuesta>(`/api/gestion/creditos/${id}/flexibilidad`, { signal }),

  registrarFlexibilidad: (
    id: number,
    cantidad: number,
  ): Promise<{ flexibilidad: number; mensaje: string }> =>
    apiClient.post<{ flexibilidad: number; mensaje: string }>(
      `/api/gestion/creditos/${id}/flexibilidad`,
      { body: { cantidad } },
    ),

  pagosCredito: (
    id: number,
    filtros: FiltrosPagos,
    signal?: AbortSignal,
  ): Promise<PagosCreditoRespuesta> =>
    apiClient.get<PagosCreditoRespuesta>(
      `/api/gestion/creditos/${id}/pagos${cadenaDe(filtros)}`,
      { signal },
    ),

  registrarPagoCredito: (
    id: number,
    datos: PeticionPagoCredito,
  ): Promise<PagoRegistradoRespuesta> =>
    apiClient.post<PagoRegistradoRespuesta>(`/api/gestion/creditos/${id}/pagos`, { body: datos }),

  liquidarCanje: (id: number, datos: PeticionLiquidarCanje): Promise<LiquidacionRespuesta> =>
    apiClient.post<LiquidacionRespuesta>(`/api/gestion/creditos/${id}/vales/liquidar`, {
      body: datos,
    }),

  enlazarVales: (
    id: number,
    datos: { vales: number[]; documento: string },
  ): Promise<{ vales: number; documento: string; mensaje: string }> =>
    apiClient.post<{ vales: number; documento: string; mensaje: string }>(
      `/api/gestion/creditos/${id}/vales/enlazar`,
      { body: datos },
    ),

  mapaCalor: (
    year: number,
    month: number,
    metric: MetricaCalor,
    signal?: AbortSignal,
  ): Promise<MapaCalorRespuesta> =>
    apiClient.get<MapaCalorRespuesta>(
      `/api/gestion/administracion/mapa-calor${cadenaDe({ year, month, metric })}`,
      { signal },
    ),
};

/** Estado de la línea de crédito por cliente. */
export function useCreditos(filtros: FiltrosCreditos) {
  return useQuery({
    queryKey: cierreKeys.creditos(filtros),
    queryFn: ({ signal }) => cierreApi.creditos(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Detalle de un crédito con sus facturas y pagos. */
export function useCredito(id: number | null, filtros: FiltrosCreditos) {
  return useQuery({
    queryKey: cierreKeys.credito(id, filtros),
    queryFn: ({ signal }) => cierreApi.credito(id as number, filtros, signal),
    // `id !== null`: el crédito 0 existe en este ejemplar.
    enabled: id !== null,
    staleTime: 30_000,
  });
}

/** Descuentos y vehículos de la APP. */
export function useClientesApp(filtros: FiltrosClientesApp) {
  return useQuery({
    queryKey: cierreKeys.clientesApp(filtros),
    queryFn: ({ signal }) => cierreApi.clientesApp(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Listado general de clientes de la APP (servicio externo). */
export function useClientesAppGenerales(activo: boolean) {
  return useQuery({
    queryKey: cierreKeys.clientesAppGenerales(),
    queryFn: ({ signal }) => cierreApi.clientesAppGenerales(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Alta de un cliente de la APP. */
export function useCrearClienteApp() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosClienteApp) => cierreApi.crearClienteApp(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes-app'] });
    },
  });
}

/** Edición de un cliente de la APP. */
export function useEditarClienteApp() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: DatosClienteApp }) =>
      cierreApi.editarClienteApp(id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'clientes-app'] });
    },
  });
}

/** Estados del crédito y productos del catálogo de combustibles. */
export function useCreditosCatalogos() {
  return useQuery({
    queryKey: cierreKeys.catalogos(),
    queryFn: ({ signal }) => cierreApi.catalogos(signal),
    staleTime: 300_000,
  });
}

/** Edita el límite, el estado, los descuentos y el código externo del crédito. */
export function useActualizarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: EdicionCredito }) =>
      cierreApi.actualizarCredito(id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Balance (estado de cuenta) del cliente de un crédito. */
export function useBalanceCredito(id: number | null, filtros: FiltrosBalance) {
  return useQuery({
    queryKey: cierreKeys.balance(id, filtros),
    queryFn: ({ signal }) => cierreApi.balance(id as number, filtros, signal),
    enabled: id !== null,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Vales de consumo pendientes de liquidar del crédito. */
export function useValesCanje(id: number | null, filtros: FiltrosValesCanje) {
  return useQuery({
    queryKey: cierreKeys.vales(id, filtros),
    queryFn: ({ signal }) => cierreApi.valesCanje(id as number, filtros, signal),
    enabled: id !== null,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Series administrativas activas del tipo de comprobante. */
export function useSeriesCanje(tipo: string) {
  return useQuery({
    queryKey: cierreKeys.seriesCanje(tipo),
    queryFn: ({ signal }) => cierreApi.seriesCanje(tipo, signal),
    staleTime: 300_000,
  });
}

/** Plazos, destinos de pago, cuentas bancarias y series por defecto del canje. */
export function useParametrosCanje() {
  return useQuery({
    queryKey: cierreKeys.parametrosCanje(),
    queryFn: ({ signal }) => cierreApi.parametrosCanje(signal),
    staleTime: 300_000,
  });
}

/** Pre-cálculo del canje: comprobantes que se emitirían con los vales elegidos. */
export function usePrecalculoCanje() {
  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: PeticionPrecalculoCanje }) =>
      cierreApi.precalculoCanje(id, datos),
  });
}

/** Facturas por cobrar, pagos registrados y catálogos del crédito. */
export function usePagosCredito(id: number | null, filtros: FiltrosPagos) {
  return useQuery({
    queryKey: cierreKeys.pagos(id, filtros),
    queryFn: ({ signal }) => cierreApi.pagosCredito(id as number, filtros, signal),
    enabled: id !== null,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Opciones del alta de una línea de crédito. */
export function useCatalogosCredito() {
  return useQuery({
    queryKey: cierreKeys.creditosCatalogos(),
    queryFn: ({ signal }) => cierreApi.catalogosCredito(signal),
    staleTime: 300_000,
  });
}

/** Movimientos de flexibilidad del crédito. */
export function useFlexibilidad(id: number | null) {
  return useQuery({
    queryKey: cierreKeys.flexibilidad(id),
    queryFn: ({ signal }) => cierreApi.flexibilidad(id as number, signal),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

/**
 * Reporte general de pagos (`Credito::report_pagos`): las facturas a crédito de
 * todos los clientes del rango, agrupadas por cliente.
 */
export function useReportePagos(filtros: FiltrosReportePagos, activo: boolean) {
  return useQuery({
    queryKey: cierreKeys.reportePagos(filtros),
    queryFn: ({ signal }) => cierreApi.reportePagos(filtros, signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Registra flexibilidad en la línea. */
export function useRegistrarFlexibilidad(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (cantidad: number) => cierreApi.registrarFlexibilidad(id as number, cantidad),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Crea una línea de crédito. */
export function useCrearCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: NuevaLineaCredito) => cierreApi.crearCredito(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Elimina una línea de crédito (sólo si no tiene vales). */
export function useEliminarCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => cierreApi.eliminarCredito(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Registra un pago del crédito (reparte el importe entre sus facturas). */
export function useRegistrarPagoCredito(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionPagoCredito) => cierreApi.registrarPagoCredito(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Emite los comprobantes de la liquidación de vales. */
export function useLiquidarCanje(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionLiquidarCanje) => cierreApi.liquidarCanje(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Enlaza los vales elegidos con un comprobante existente. */
export function useEnlazarVales(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { vales: number[]; documento: string }) =>
      cierreApi.enlazarVales(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'creditos'] });
    },
  });
}

/** Mapa de calor de surtidores del mes y la métrica elegidos. */
export function useMapaCalor(year: number, month: number, metric: MetricaCalor) {
  return useQuery({
    queryKey: cierreKeys.calor(year, month, metric),
    queryFn: ({ signal }) => cierreApi.mapaCalor(year, month, metric, signal),
    staleTime: 60_000,
  });
}

/** Catálogos del alta de descuentos y vehículos de la APP. */
export function useCatalogosClientesApp() {
  return useQuery({
    queryKey: cierreKeys.catalogosClientesApp(),
    queryFn: ({ signal }) => cierreApi.catalogosClientesApp(signal),
    staleTime: 300_000,
  });
}

/** Vehículos activos de un cliente de la APP. */
export function useVehiculosDeCliente(clienteId: number | null) {
  return useQuery({
    queryKey: cierreKeys.vehiculosCliente(clienteId),
    queryFn: ({ signal }) => cierreApi.vehiculosDeCliente(clienteId as number, signal),
    enabled: clienteId !== null,
    staleTime: 30_000,
  });
}

/** Invalida el listado de Clientes APP y sus catálogos. */
function useInvalidarClientesApp() {
  const clienteConsultas = useQueryClient();

  return () => {
    void clienteConsultas.invalidateQueries({ queryKey: [CLAVE, 'clientes-app'] });
  };
}

/** Da de alta un vehículo de la APP. */
export function useCrearVehiculoApp() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: ({ clienteId, datos }: { clienteId: number; datos: DatosVehiculoApp }) =>
      cierreApi.crearVehiculoApp(clienteId, datos),
    onSuccess: invalidar,
  });
}

/** Da de baja un vehículo de la APP y los descuentos que dependían de él. */
export function useEliminarVehiculoApp() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: (vehiculoId: number) => cierreApi.eliminarVehiculoApp(vehiculoId),
    onSuccess: invalidar,
  });
}

/** Da de alta un descuento de la APP. */
export function useCrearDescuentoApp() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: (datos: DatosDescuentoApp) => cierreApi.crearDescuentoApp(datos),
    onSuccess: invalidar,
  });
}

/** Edita un descuento de la APP. */
export function useActualizarDescuentoApp() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: DatosDescuentoApp }) =>
      cierreApi.actualizarDescuentoApp(id, datos),
    onSuccess: invalidar,
  });
}

/** Da de baja un descuento de la APP. */
export function useEliminarDescuentoApp() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: (id: number) => cierreApi.eliminarDescuentoApp(id),
    onSuccess: invalidar,
  });
}

/** Da de baja varios descuentos de la APP. */
export function useEliminarDescuentosMasivo() {
  const invalidar = useInvalidarClientesApp();

  return useMutation({
    mutationFn: (ids: number[]) => cierreApi.eliminarDescuentosMasivo(ids),
    onSuccess: invalidar,
  });
}
